import React, { useState, useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title,
} from 'chart.js';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import TransactionModal from '../Transactions/TransactionModal';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const catColors = ['#c9a227','#1d9e75','#e24b4a','#378ADD','#8b5cf6','#f97316','#0d1b2a','#993556'];

export default function CardReport() {
  const { state } = useApp();
  const [selectedCardId, setSelectedCardId] = useState(state.cards[0]?.id || '');
  const [editTx, setEditTx] = useState(null);

  const card = state.cards.find(c => c.id === selectedCardId);
  const cats = useMemo(() => Object.fromEntries(state.categories.map(c => [c.id, c])), [state.categories]);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  // All card transactions
  const cardTxs = useMemo(() =>
    state.transactions
      .filter(t => t.cardId === selectedCardId && t.type === 'expense')
      .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [state.transactions, selectedCardId]
  );

  // This month's transactions
  const thisMonthTxs = useMemo(() =>
    cardTxs.filter(t => t.date.startsWith(currentMonth)),
    [cardTxs, currentMonth]
  );

  // Spending by category (this month)
  const byCat = useMemo(() => {
    const map = {};
    thisMonthTxs.forEach(t => { map[t.categoryId] = (map[t.categoryId] || 0) + t.value; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [thisMonthTxs]);

  const totalThisMonth = thisMonthTxs.reduce((s, t) => s + t.value, 0);

  // Last 6 months trend
  const trendMonths = useMemo(() => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
      const total = cardTxs.filter(t => t.date.startsWith(key)).reduce((s, t) => s + t.value, 0);
      return { key, label, total };
    });
  }, [cardTxs]); // eslint-disable-line

  const catBgColors = { alim:'#FAEEDA', transp:'#E6F1FB', moradia:'#FCEBEB', saude:'#FCEBEB', sal:'#EAF3DE', free:'#EAF3DE', inv:'#EAF3DE', contas:'#E6F1FB', lazer:'#FBEAF0', cartao:'#FBEAF0', educ:'#EAF3DE', default:'#F1EFE8' };
  const catIconColors = { alim:'#854F0B', transp:'#185FA5', moradia:'#e24b4a', saude:'#e24b4a', sal:'#3B6D11', free:'#3B6D11', inv:'#3B6D11', contas:'#378ADD', lazer:'#993556', cartao:'#A32D2D', educ:'#3B6D11', default:'#888780' };

  if (state.cards.length === 0) {
    return (
      <div className="main-content">
        <div className="page-header">
          <div><h1 className="page-title">Relatório de Cartão</h1></div>
        </div>
        <div className="empty-state" style={{ marginTop: 60 }}>
          <i className="fas fa-credit-card" style={{ fontSize: 40, marginBottom: 12 }}></i>
          <div>Nenhum cartão cadastrado ainda.</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Adicione um cartão em Contas e Cartões.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Relatório de Cartão</h1>
          <p className="page-subtitle">Análise detalhada dos gastos no cartão</p>
        </div>
      </div>

      {/* Card selector */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {state.cards.map(c => (
          <button
            key={c.id}
            onClick={() => setSelectedCardId(c.id)}
            style={{
              padding: '8px 16px', borderRadius: 10, border: '2px solid',
              borderColor: selectedCardId === c.id ? c.color || '#c9a227' : 'var(--border)',
              background: selectedCardId === c.id ? `${c.color || '#c9a227'}18` : 'var(--card-bg)',
              color: selectedCardId === c.id ? c.color || '#c9a227' : 'var(--text)',
              fontWeight: selectedCardId === c.id ? 700 : 500,
              cursor: 'pointer', fontSize: 13, transition: 'all 0.15s',
            }}
          >
            <i className="fas fa-credit-card" style={{ marginRight: 6 }}></i>
            {c.name} •• {c.lastDigits}
          </button>
        ))}
      </div>

      {card && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            <div className="metric-card">
              <div className="metric-card-top">
                <span className="metric-card-label">Fatura Atual</span>
                <div className="metric-card-icon red"><i className="fas fa-file-invoice"></i></div>
              </div>
              <div className="metric-card-value red">{formatCurrency(totalThisMonth)}</div>
              <div className="metric-card-footer">Gastos este mês</div>
            </div>
            <div className="metric-card">
              <div className="metric-card-top">
                <span className="metric-card-label">Limite Usado</span>
                <div className="metric-card-icon" style={{ background: '#fef3c7' }}><i className="fas fa-gauge" style={{ color: '#c9a227' }}></i></div>
              </div>
              <div className="metric-card-value" style={{ color: '#c9a227' }}>{formatCurrency(card.used || 0)}</div>
              <div className="metric-card-footer">de {formatCurrency(card.limit)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-card-top">
                <span className="metric-card-label">Disponível</span>
                <div className="metric-card-icon green"><i className="fas fa-wallet"></i></div>
              </div>
              <div className="metric-card-value green">{formatCurrency(Math.max(0, card.limit - (card.used || 0)))}</div>
              <div className="metric-card-footer">Limite disponível</div>
            </div>
            <div className="metric-card">
              <div className="metric-card-top">
                <span className="metric-card-label">Vencimento</span>
                <div className="metric-card-icon" style={{ background: '#e0f2fe' }}><i className="fas fa-calendar" style={{ color: '#378ADD' }}></i></div>
              </div>
              <div className="metric-card-value" style={{ color: '#378ADD' }}>Dia {card.dueDay}</div>
              <div className="metric-card-footer">Fechamento dia {card.closeDay}</div>
            </div>
          </div>

          {/* Charts row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 20 }}>
            {/* Pie by category */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div>
                  <div className="dash-card-title">Gastos por Categoria</div>
                  <div className="dash-card-sub">Mês atual</div>
                </div>
              </div>
              {byCat.length === 0 ? (
                <div className="dash-empty"><i className="fas fa-chart-pie" style={{ fontSize: 28, marginBottom: 8, color: '#d1d5db' }}></i><div>Sem gastos este mês</div></div>
              ) : (
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ width: 150, height: 150, flexShrink: 0 }}>
                    <Doughnut
                      data={{
                        labels: byCat.map(([id]) => cats[id]?.name || id),
                        datasets: [{ data: byCat.map(([, v]) => v), backgroundColor: catColors, borderWidth: 2, borderColor: 'var(--card-bg)' }],
                      }}
                      options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed) } } }, cutout: '60%' }}
                    />
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {byCat.slice(0, 5).map(([id, val], i) => (
                      <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: catColors[i % catColors.length], flexShrink: 0 }}></span>
                        <span style={{ flex: 1, fontSize: 12, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cats[id]?.name || id}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', flexShrink: 0 }}>{Math.round(val / totalThisMonth * 100)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bar chart - last 6 months */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div>
                  <div className="dash-card-title">Histórico de Gastos</div>
                  <div className="dash-card-sub">Últimos 6 meses</div>
                </div>
              </div>
              <div style={{ height: 180 }}>
                <Bar
                  data={{
                    labels: trendMonths.map(m => m.label),
                    datasets: [{
                      label: 'Gastos',
                      data: trendMonths.map(m => m.total),
                      backgroundColor: trendMonths.map(m => m.key === currentMonth ? '#c9a227' : '#c9a22740'),
                      borderRadius: 6,
                    }],
                  }}
                  options={{
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ' ' + formatCurrency(ctx.parsed.y) } } },
                    scales: { y: { beginAtZero: true, ticks: { callback: v => 'R$' + (v >= 1000 ? (v/1000).toFixed(1)+'k' : v) } } },
                  }}
                />
              </div>
            </div>
          </div>

          {/* Transaction list */}
          <div className="dash-card">
            <div className="dash-card-header">
              <div>
                <div className="dash-card-title">Lançamentos no Cartão</div>
                <div className="dash-card-sub">{cardTxs.length} transações registradas</div>
              </div>
            </div>
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {cardTxs.length === 0 ? (
                <div className="dash-empty"><i className="fas fa-receipt" style={{ fontSize: 28, marginBottom: 8, color: '#d1d5db' }}></i><div>Nenhum lançamento</div></div>
              ) : cardTxs.map(tx => {
                const cat = cats[tx.categoryId];
                return (
                  <div
                    key={tx.id}
                    className="tx-item"
                    onClick={() => setEditTx(tx)}
                    style={{ cursor: 'pointer', gap: 10 }}
                  >
                    <div className="tx-icon" style={{ background: catBgColors[tx.categoryId] || catBgColors.default }}>
                      <i className={cat?.icon || 'fas fa-circle'} style={{ color: catIconColors[tx.categoryId] || catIconColors.default }}></i>
                    </div>
                    <div className="tx-info">
                      <div className="tx-desc">{tx.description}</div>
                      <div className="tx-cat">{cat?.name} · {tx.date}</div>
                    </div>
                    <div className="tx-right">
                      <div className="tx-amount expense">{formatCurrency(tx.value)}</div>
                      <div style={{ fontSize: 10, color: tx.status === 'paid' ? '#1d9e75' : '#f97316', fontWeight: 600 }}>
                        {tx.status === 'paid' ? 'Pago' : 'Pendente'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {editTx && <TransactionModal tx={editTx} onClose={() => setEditTx(null)} />}
    </div>
  );
}
